import React, { ReactElement, useEffect, useMemo } from 'react';
import Modal from 'antd/lib/modal';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import { UserProfile, UserButton } from '@clerk/clerk-react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import UserManagement from './UserManagement/UserManagement';
import { ReactComponent as UserManagementIcon } from '@src/assets/img/menus/user-management-icon.svg';
import { ReactComponent as OrganizationIcon } from '@src/assets/img/menus/organization-info.svg';
import { ReactComponent as HubSecretsIcon } from '@src/assets/img/menus/hub-secrets.svg';
import OrganizationDetails from './OrganizationDetails/OrganizationDetails';
import HubSecrets from './HubSecrets/HubSecrets';
import ApiKeys from './ApiKeys/ApiKeys';
import KeyOutlined from '@ant-design/icons/KeyOutlined';
import styles from './UserAccountButton.module.css';
import useTranslation from '@hooks/useTranslation';
import Icon from '@ant-design/icons';
import useAuthenticatedUser from '@hooks/useAuthenticatedUser';
import { AccountMenuProvider, useAccountMenuContext } from './AccountMenuContext';
import { selectUserProfileModalVisible, showUserProfileModal } from '@src/features/interface/interfaceSlice';

type Props = {
  className?: string;
}

/**
 * Internal component that uses the AccountMenu context
 * Separated to avoid context usage in the main component
 */
const UserProfileContent: React.FC = () => {
  const user = useAuthenticatedUser();
  const location = useLocation();
  const t = useTranslation('AccountMenu.Modal');
  const { handleFetchMembers, handleFetchOrganizationDetails } = useAccountMenuContext();
  
  // Extract current page from URL hash (React Router way)
  const currentPage = useMemo(() => {
    const hash = location.hash || window.location.hash;
    return hash.replace('#/', '').replace('#', '');
  }, [location.hash]);

  /**
   * Fetches the list of organization members or organization details from the API based on the current page
   */
  useEffect(() => {
    if(currentPage === 'members'){
      handleFetchMembers();
    } else if(currentPage === 'org-details'){
      handleFetchOrganizationDetails();
    }
  }, [currentPage, handleFetchMembers, handleFetchOrganizationDetails]);

  return (
    <UserProfile 
      routing='hash'
      // path={'/'}
      appearance={{
        elements: {
          rootBox: styles.ClerkRootBox,
          headerTitle: styles.ClerkHeaderTitle,
          scrollBox: styles.ClerkScrollBox,
          headerSubtitle: styles.ClerkHeaderSubtitle,
          socialButtonsBlockButton: styles.ClerkSocialButton,
          formButtonPrimary: styles.ClerkSubmitButton,
          formFieldInput: styles.ClerkInput,
          formFieldLabel: styles.ClerkLabel,
          identityPreviewText: styles.ClerkIdentityText,
          formResendCodeLink: styles.ClerkLink,
          footerActionLink: styles.ClerkFooterLink,
          cardBox: styles.ClerkCardBox,
          formFieldInputShowPasswordButton: styles.ClerkShowPasswordButton,
        },
      }}
    >
      {(user.isOrganizationOwner || user.isOrganizationAdmin) && (
        <UserProfile.Page key={'users'} label={t('UserManagement')} url="members" labelIcon={<Icon component={UserManagementIcon} />}>
          <UserManagement />
        </UserProfile.Page>
      )}

      {user.isOrganizationOwner && (
        <UserProfile.Page key={'organization'} label={t('OrganizationDetails')} url="org-details" labelIcon={<Icon component={OrganizationIcon} />}>
          <OrganizationDetails />
        </UserProfile.Page>
      )}

      {user.isOrganizationOwner && (
        <UserProfile.Page key={'api-keys'} label={t('ApiKeys')} url="api-keys" labelIcon={<KeyOutlined />}>
          <ApiKeys />
        </UserProfile.Page>
      )}

      <UserProfile.Page key={'hub-secrets'} label={t('HubSecrets')} url="hub-secrets" labelIcon={<Icon component={HubSecretsIcon} />}>
        <HubSecrets />
      </UserProfile.Page>
    </UserProfile>
  );
};

const UserAccountButton = (props: Props): ReactElement => {
  const dispatch = useDispatch();
  const userProfileModalVisible = useSelector(selectUserProfileModalVisible);
  const t = useTranslation('AccountMenu.Modal');

  /**
   * Opens the user profile modal
   */
  const openProfile = () => {
    dispatch(showUserProfileModal(''));
  };

  /**
   * Closes the user profile modal
   */
  const closeProfile = () => {
    dispatch(showUserProfileModal(null));
  };

  /**
   * Handle navigation to specific page when modal opens with a target path
   */
  useEffect(() => {
    if (userProfileModalVisible && userProfileModalVisible !== '') {
      // Navigate to the specified path
      setTimeout(() => {
        window.location.hash = `#/${userProfileModalVisible}`;
      }, 100);
    }
  }, [userProfileModalVisible]);

  
  return (
    <div className={props.className || ''}>
      {/* <Button type="text" icon={<UserOutlined style={{ fontSize: 26 }} />} onClick={openProfile} /> */}
      <UserButton 
        appearance={{
          elements: {
            // IMPORTANT: custom class to hide the default manage account button
            userButtonPopoverActions: styles.UserButtonPopoverActions,
          },
        }}
      >
        <UserButton.MenuItems>
          <UserButton.Action 
            label={t('AccountManagement')} 
            labelIcon={<SettingOutlined />} 
            onClick={openProfile}
          />
        </UserButton.MenuItems>
        
      </UserButton>

      {/* We use our own modal to have more control over the styling */}
      <Modal
        open={userProfileModalVisible !== null}
        onCancel={closeProfile}
        footer={null}
        width={'auto'}
        // mask={false}
        destroyOnClose
        maskClosable={false}
        centered
        classNames={{
          content: styles.UserProfileModal,
        }}
      >
        <AccountMenuProvider>
          <UserProfileContent />
        </AccountMenuProvider>
      </Modal>
    </div>
  );
};

export default UserAccountButton;


